import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, IdentificationCard } from "@phosphor-icons/react";

const TITLES = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  dpa: "Data Processing Agreement",
};

/**
 * Renders a markdown legal document fetched from /api/legal/{slug}.
 * Minimal dependency-free markdown rendering — supports headings, bold,
 * italic, links, lists, tables, code, blockquotes. Good enough for
 * static legal docs without pulling in a 50kb markdown lib.
 */
function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => escape(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.+?)`/g, '<code class="bg-zinc-100 text-zinc-800 px-1 py-0.5 rounded text-[0.92em]">$1</code>')
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-zinc-900 underline">$1</a>');
  while (i < lines.length) {
    const ln = lines[i];
    if (/^# /.test(ln))      { out.push(`<h1 class="font-display font-bold text-4xl mt-12 mb-4 text-zinc-950">${inline(ln.slice(2))}</h1>`); i++; continue; }
    if (/^## /.test(ln))     { out.push(`<h2 class="font-display font-bold text-2xl mt-10 mb-3 text-zinc-900">${inline(ln.slice(3))}</h2>`); i++; continue; }
    if (/^### /.test(ln))    { out.push(`<h3 class="font-semibold text-lg mt-6 mb-2 text-zinc-800">${inline(ln.slice(4))}</h3>`); i++; continue; }
    if (/^---/.test(ln))     { out.push(`<hr class="my-8 border-zinc-200"/>`); i++; continue; }
    if (/^> /.test(ln))      { out.push(`<blockquote class="border-l-2 border-amber-400 bg-amber-50/50 pl-4 py-2 my-4 text-sm text-zinc-700">${inline(ln.slice(2))}</blockquote>`); i++; continue; }
    if (/^\|/.test(ln))      {
      // Collect table lines.
      const rows = [];
      while (i < lines.length && /^\|/.test(lines[i])) { rows.push(lines[i]); i++; }
      const cells = rows.map(r => r.split("|").slice(1, -1).map(c => c.trim()));
      const header = cells[0]; const body = cells.slice(2);
      out.push('<div class="overflow-x-auto my-4"><table class="w-full text-sm border-collapse">');
      out.push('<thead><tr>' + header.map(c => `<th class="border-b border-zinc-300 px-3 py-2 text-left font-semibold text-zinc-700">${inline(c)}</th>`).join("") + '</tr></thead>');
      out.push('<tbody>' + body.map(r => '<tr>' + r.map(c => `<td class="border-b border-zinc-100 px-3 py-2 text-zinc-700 align-top">${inline(c)}</td>`).join("") + '</tr>').join("") + '</tbody>');
      out.push('</table></div>');
      continue;
    }
    if (/^- /.test(ln) || /^\* /.test(ln)) {
      const items = [];
      while (i < lines.length && (/^- /.test(lines[i]) || /^\* /.test(lines[i]))) {
        items.push(lines[i].replace(/^[-*] /, ""));
        i++;
      }
      out.push('<ul class="list-disc pl-6 my-3 space-y-1 text-zinc-700 text-[15px] leading-relaxed">' + items.map(t => `<li>${inline(t)}</li>`).join("") + "</ul>");
      continue;
    }
    if (/^\d+\.\s/.test(ln)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      out.push('<ol class="list-decimal pl-6 my-3 space-y-1 text-zinc-700 text-[15px] leading-relaxed">' + items.map(t => `<li>${inline(t)}</li>`).join("") + "</ol>");
      continue;
    }
    if (ln.trim() === "")    { out.push(""); i++; continue; }
    out.push(`<p class="my-3 text-zinc-700 text-[15px] leading-relaxed">${inline(ln)}</p>`);
    i++;
  }
  return out.join("\n");
}

export default function Legal() {
  const { slug = "terms" } = useParams();
  const [content, setContent] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/legal/${slug}`);
        setContent(r.data?.content || "");
      } catch (e) {
        setErr("Document not found.");
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-white" data-testid={`legal-page-${slug}`}>
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between py-4 px-6">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="legal-home">
            <div className="w-8 h-8 bg-zinc-950 text-white flex items-center justify-center rounded-sm">
              <IdentificationCard weight="fill" size={18}/>
            </div>
            <span className="font-display font-bold text-lg text-zinc-950">SaarOne</span>
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/legal/terms" className={slug === "terms" ? "text-zinc-950 font-medium" : "text-zinc-500 hover:text-zinc-900"} data-testid="legal-nav-terms">Terms</Link>
            <Link to="/legal/privacy" className={slug === "privacy" ? "text-zinc-950 font-medium" : "text-zinc-500 hover:text-zinc-900"} data-testid="legal-nav-privacy">Privacy</Link>
            <Link to="/legal/dpa" className={slug === "dpa" ? "text-zinc-950 font-medium" : "text-zinc-500 hover:text-zinc-900"} data-testid="legal-nav-dpa">DPA</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-8 pb-24">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-6" data-testid="legal-back">
          <ArrowLeft size={14}/> Back to home
        </Link>
        {err && <div className="text-red-600">{err}</div>}
        {!err && !content && <div className="text-zinc-500">Loading…</div>}
        {content && <article className="font-sans" dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}/>}
      </main>

      <footer className="border-t border-zinc-200 py-8 text-center text-xs text-zinc-500">
        © 2026 SaarOne Technologies. {TITLES[slug] || ""}.
      </footer>
    </div>
  );
}
